import { useEffect, useMemo, useState } from 'react';
import EmailConsumerAuthModal from '../components/auth/EmailConsumerAuthModal';
import { useWebConsumerSessionBootstrap } from '../hooks/useWebConsumerSessionBootstrap';
import { useWebConsumerAuthStore } from '../stores/useWebConsumerAuthStore';

interface BridgeConfig {
  enabled: boolean;
  client_ids: string[];
  protocol_version: number;
}

function authorizationInput() {
  const query = new URLSearchParams(window.location.search);
  const clientId = query.get('client_id') || '';
  const redirect = query.get('redirect_uri') || '';
  const device = query.get('browser_client_id') || '';
  const challenge = query.get('code_challenge') || '';
  const state = query.get('state') || '';
  if (!/^[a-p]{32}$/.test(clientId) || redirect !== `https://${clientId}.chromiumapp.org/browser-agent-auth`
    || !/^[a-zA-Z0-9_-]{16,96}$/.test(device) || !/^[a-zA-Z0-9_-]{43}$/.test(challenge)
    || !/^[a-zA-Z0-9_-]{32,128}$/.test(state)) return null;
  return { client_id: clientId, redirect_uri: redirect, browser_client_id: device, code_challenge: challenge, state };
}

export default function BrowserAgentAuthorizePage() {
  const input = useMemo(authorizationInput, []);
  const [config, setConfig] = useState<BridgeConfig | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const currentUser = useWebConsumerAuthStore((state) => state.currentUser);
  const setCurrentUser = useWebConsumerAuthStore((state) => state.setCurrentUser);
  const { isSessionResolved } = useWebConsumerSessionBootstrap('houmao', { enabled: !!input });

  useEffect(() => {
    if (!input) return;
    let active = true;
    void fetch('/api/v1/browser-agent/config', { credentials: 'include', cache: 'no-store', redirect: 'error' })
      .then(async (response) => {
        if (!response.ok || !(response.headers.get('content-type') || '').includes('application/json')) throw new Error();
        const result = await response.json() as BridgeConfig;
        if (active) setConfig(result);
      })
      .catch(() => { if (active) setError('无法读取插件授权配置，请关闭窗口后重试。'); });
    return () => { active = false; };
  }, [input]);

  const allowed = !!input && config?.enabled === true && config.protocol_version === 1
    && Array.isArray(config.client_ids) && config.client_ids.includes(input.client_id);

  const authorize = async () => {
    if (!input || !allowed || busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch('/api/v1/browser-agent/auth/authorize', {
        method: 'POST', credentials: 'include', redirect: 'error', cache: 'no-store',
        headers: { 'content-type': 'application/json' }, body: JSON.stringify(input),
        signal: AbortSignal.timeout(15000),
      });
      if (response.status === 401) {
        setLoginOpen(true);
        throw new Error('登录已失效，请重新登录后确认授权。');
      }
      if (!response.ok || !(response.headers.get('content-type') || '').includes('application/json')) {
        throw new Error('授权未完成，请重试。');
      }
      const result = await response.json() as { redirect_url?: string };
      const callback = new URL(result.redirect_url || '');
      const expected = new URL(input.redirect_uri);
      if (callback.origin !== expected.origin || callback.pathname !== expected.pathname
        || callback.searchParams.get('state') !== input.state || !callback.searchParams.get('code')) {
        throw new Error('授权回调校验失败，请关闭窗口后重试。');
      }
      window.location.replace(callback.href);
    } catch (failure) {
      setError(failure instanceof Error && failure.message.startsWith('授权') ? failure.message : '授权未完成，请重试或重新登录。');
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-xl space-y-6 px-6 py-12 text-stone-800">
      <h1 className="text-3xl font-semibold">授权浏览器金箍</h1>
      <p>允许当前插件使用本账号连接浏览器 Agent、执行您提交的任务，并读取本账号的运行记录。</p>
      <p className="text-sm text-stone-600">仅授权这台浏览器。网站登录密码和模型 API Key 不会提供给插件。任务按本账号的权限与积分计费。</p>
      {!input && <p role="alert">授权链接无效，请从插件点击「登录并连接」。</p>}
      {input && config && !allowed && <p role="alert">当前服务尚未开放此插件版本，请联系管理员。</p>}
      {input && !config && !error && <p role="status">正在读取授权配置…</p>}
      {allowed && !isSessionResolved && <p role="status">正在读取登录状态…</p>}
      {allowed && isSessionResolved && !currentUser && (
        <button className="rounded-lg bg-stone-900 px-5 py-3 text-white" onClick={() => setLoginOpen(true)}>登录账号</button>
      )}
      {allowed && isSessionResolved && currentUser && (
        <>
          <p>当前账号：{currentUser.displayName || currentUser.username || currentUser.email || currentUser.id}</p>
          <button className="rounded-lg bg-stone-900 px-5 py-3 text-white disabled:opacity-50" disabled={busy} onClick={() => void authorize()}>
            {busy ? '正在授权…' : '允许并连接'}
          </button>
        </>
      )}
      {error && <p role="alert">{error}</p>}
      {input && (
        <button className="block text-sm underline" onClick={() => {
          const callback = new URL(input.redirect_uri);
          callback.searchParams.set('error', 'access_denied');
          callback.searchParams.set('state', input.state);
          window.location.replace(callback.href);
        }}>取消授权</button>
      )}
      <EmailConsumerAuthModal isOpen={loginOpen} workspace="houmao" guestConversations={[]}
        onClose={() => setLoginOpen(false)} performHandoff={async () => {}}
        onAuthenticated={(user) => { setCurrentUser(user); }} />
    </main>
  );
}
